import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Sidebar.module.css";
import { CiViewBoard, CiSettings } from "react-icons/ci";
import { TbEngine } from "react-icons/tb";
import useAuth from "../hooks/useAuth.ts";
import { useSidebar } from "../context/SidebarProvider.tsx";
import { FiActivity } from "react-icons/fi";

type NavItem = {
  label: string;
  path: string;
  requiresRole?: string;
  icon: React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
  }>;
};

function Sidebar() {
  const { roles } = useAuth();
  const { collapsed, setCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setIsMobileLayout(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const navItems: NavItem[] = useMemo(
    () => [
      { label: "Pump Eff", path: "/pump-eff", icon: TbEngine },
      { label: "Pump Data", path: "/fleet", icon: CiViewBoard },
      { label: "Pump Dashboard", path: "/pump-dashboard", icon: FiActivity },
      { label: "Asset View", path: "/asset-view", icon: FiActivity },
      { label: "Interlocks", path: "/interlocks", icon: FiActivity },
      {
        label: "Flagged Re-engagement Events",
        path: "/flagged-reengagement-events",
        icon: FiActivity,
      },
      {
        label: "Users",
        path: "/settings",
        requiresRole: "Manager",
        icon: CiSettings,
      },
    ],
    [],
  );

  const visibleItems = navItems.filter(
    (item) => !item.requiresRole || roles?.includes(item.requiresRole),
  );

  const aside = (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}
      aria-label="Sidebar"
    >
      <nav className={styles.sidebarList} role="navigation">
        {visibleItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);
          const Icon = item.icon;

          return (
            <button
              key={item.path}
              type="button"
              title={collapsed ? item.label : undefined}
              onClick={() => {
                navigate(item.path);
                if (isMobileLayout) setCollapsed(true);
              }}
              className={`${styles.row} ${isActive ? styles.active : styles.notActive}`}
            >
              {isActive && (
                <span className={styles.activeIndicator} aria-hidden />
              )}
              <Icon
                className={styles.icon}
                style={{ width: "18px", height: "18px" }}
              />
              <span
                className={`${styles.title} ${collapsed ? styles.titleCollapsed : ""}`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>
    </aside>
  );

  if (!isMobileLayout) {
    return aside;
  }

  return (
    <>
      {!collapsed && (
        <button
          type="button"
          className={styles.sidebarBackdrop}
          aria-label="Close menu"
          onClick={() => setCollapsed(true)}
        />
      )}
      {aside}
    </>
  );
}

export default Sidebar;
